<script lang="ts" setup>
import type { BonusWalletData } from '@/interfaces/IBonusWallet'
import { amountHelper } from '@nami/domain/classes'
import { storeToRefs } from 'pinia'
import IconBonusWallet from '@/components/icons/IconBonusWallet.vue'
import IconMainWallet from '@/components/icons/IconMainWallet.vue'
import { useUserStore } from '@/stores/userStore'
import CircularProgress from './CircularProgress.vue'

interface Props {
    data: BonusWalletData
}

const { data } = defineProps<Props>()

const userStore = useUserStore()
const { userInfo } = storeToRefs(userStore)

const formatCurrency = (amount: number) => {
    return amountHelper.getLocaleStringWithSymbol(amount, userInfo.value.currency.toString(), 2)
}
</script>

<template>
    <div class="bonus-wallet">
        <div class="wallet-section">
            <div class="wallet-item wallet-item-main">
                <div class="wallet-icon">
                    <IconMainWallet />
                </div>
                <div class="wallet-label">
                    Main Wallet
                </div>
                <div class="wallet-amount">
                    {{ formatCurrency(data.mainWalletBalance) }}
                </div>
            </div>
            <div class="wallet-item wallet-item-bonus">
                <div class="bonus-wallet-icon">
                    <IconBonusWallet />
                </div>
                <div class="wallet-label">
                    Bonus Wallet
                </div>
            </div>
        </div>
        <div class="bonus-list">
            <div v-for="item in data.bonusItems" :key="item.id" class="bonus-item">
                <CircularProgress :percentage="item.progressPercentage" class="circular-progress-wrapper" />
                <div class="bonus-content">
                    <div class="bonus-title">
                        {{ item.title }}
                    </div>
                    <div class="bonus-content-box">
                        <div class="bonus-progress">
                            <b>{{ formatCurrency(item.currentAmount) }}</b>
                            <span>/</span>
                            <span>{{ formatCurrency(item.targetAmount) }}</span>
                        </div>
                        <div class="bonus-redeemable">
                            {{ formatCurrency(item.redeemableAmount) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/common/variables.scss" as *;

.bonus-wallet {
    position: relative;
    display: flex;
    min-width: 270px;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    padding: 12px;
    background-image: linear-gradient(to bottom, $indigo-100, $blue-100);
    color: $brandColor-800;
    gap: 8px;

    &::before {
        position: absolute;
        z-index: 0;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjIwIiBoZWlnaHQ9IjI0OSIgdmlld0JveD0iMCAwIDIyMCAyNDkiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik02LjU0MTU1IDI0OUg4Ni43NzU5QzkwLjYzODEgMjQ5IDk0LjMxNTYgMjQ3LjI4NCA5Ni43OTI5IDI0NC4zMDFMMTgwLjM3NiAxNDQuMjI1QzE4NS4wMjMgMTM4LjY2IDE4NC4zIDEzMC4zNzQgMTc4Ljc2IDEyNS43MDZMMTQ1LjcyNCA5Ny44NjU2TDIxOC40NTkgMTAuODA1M0MyMjIuMDI5IDYuNTIzMzQgMjE4Ljk5OCAwIDIxMy40NDMgMEwxMzMuMjA5IDBDMTI5LjM0NyAwIDEyNS42NjkgMS43MTU4NiAxMjMuMTkyIDQuNjk5MjhMNTAuMTQ4OCA5Mi4xNDYxQzQ1LjUwMTkgOTcuNzExIDQ2LjIyNTEgMTA1Ljk5NyA1MS43NjQ1IDExMC42NjVMODQuODAwNyAxMzguNTA1TDEuNTQwNzIgMjM4LjE5NUMtMi4wMjkxIDI0Mi40NzcgMS4wMDIxNyAyNDkgNi41NTY5NCAyNDlINi41NDE1NVoiIGZpbGw9InVybCgjcGFpbnQwX2xpbmVhcl84NTgwXzM3MDEpIi8+CjxkZWZzPgo8bGluZWFyR3JhZGllbnQgaWQ9InBhaW50MF9saW5lYXJfODU4MF8zNzAxIiB4MT0iMTUyLjMwOCIgeTE9IjMwLjQyMDgiIHgyPSItMC40MTUyOSIgeTI9IjM1NC4xNSIgZ3JhZGllbnRVbml0cz0idXNlclNwYWNlT25Vc2UiPgo8c3RvcCBzdG9wLWNvbG9yPSIjOTlENUZGIiBzdG9wLW9wYWNpdHk9IjAiLz4KPHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSIjOTlENUZGIiBzdG9wLW9wYWNpdHk9IjAuOCIvPgo8L2xpbmVhckdyYWRpZW50Pgo8L2RlZnM+Cjwvc3ZnPgo=");
        background-position: center left 50px;
        background-repeat: no-repeat;
        background-size: 120% 120%;
        content: "";
        pointer-events: none;
    }

    > * {
        position: relative;
        z-index: 1;
    }
}

.wallet-section {
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: 8px;
}

.wallet-item {
    display: flex;
    align-items: center;
    border-radius: 8px;
    background-color: rgb(255, 255, 255, 10%);
    gap: 8px;
}

.wallet-item-main {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 4px;

    .wallet-icon {
        width: 16px;
        height: 16px;
    }
}

.wallet-item-bonus {
    display: flex;
    width: 100%;
    min-height: auto;
    align-items: center;
    gap: 4px;

    .bonus-wallet-icon {
        width: 16px;
        height: 16px;
    }
}

.wallet-icon,
.bonus-wallet-icon {
    display: flex;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
}

.wallet-label {
    display: flex;
    flex: 1 0 0;
    align-items: center;
    color: #002e6c;
    font-size: 14px;
    font-weight: 500;
}

.wallet-amount {
    margin-left: auto;
    color: var(--brandColor-brandColor-800-002E6C, #002e6c);
    font-family: var(--font-family-fontFamily-englishLike, "Noto Sans");
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%;
}

.bonus-list {
    display: flex;
    max-height: 300px;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    padding: 4px 8px;
    border-radius: 8px;
    background-image: linear-gradient(to bottom, rgb(255 255 255 / 90%), rgb(255 255 255 / 50%));
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-thumb {
        border-radius: 2px;
        background-color: #c8c8c8;
    }

    &::-webkit-scrollbar-track {
        border-radius: 2px;
        background-color: #ffffff;
    }
}

.bonus-item {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: 6px 0;
    border-bottom: 1px solid var(--blue-blue-100-E6F4FF, #e6f4ff);

    &:last-child {
        border-bottom: none;
    }
}

.bonus-content {
    display: flex;
    width: 100%;
    flex: 1;
    flex-direction: column;
    font-size: 14px;
    gap: 2px;
}

.bonus-title {
    color: #002e6c;
    line-height: 1.5;
}

.bonus-content-box {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
}

.bonus-progress {
    display: flex;
    align-items: center;
    color: $brandColor-600;
    font-size: 12px;
    gap: 6px;
    white-space: nowrap;

    b {
        font-weight: bold;
    }
}

.circular-progress-wrapper {
    margin-right: 8px;
}

.bonus-redeemable {
    flex: 1;
    color: #002e6c;
    font-size: 14px;
    font-weight: bold;
    line-height: 1.3;
    text-align: right;
    white-space: nowrap;
}
</style>
